import { defineStore } from 'pinia'
import { ref } from 'vue'
import getPiniaInstance from '@app/shared/pinia/instance'

interface InsightsRootInit {
  allCompaniesCount?: number
  lite?: boolean
  queryOverviewEnabled?: boolean
  recentSegments?: number[]
  remarkTags?: Record<string, unknown>
  firmWorkflows?: unknown[]
}

/**
 * Parse the five Insights dataset attributes and call useInsightsRootStore().init().
 * Colocated here so all four entry points (companies.js, insights-drawer.ts,
 * insights-subnav-left.ts, insights-subnav-right.ts) share a single place to
 * update when InsightsRootInit fields change.
 */
export function initInsightsRootFromDataset(dataset: DOMStringMap): void {
  const {
    allCompaniesCount,
    liteInsights,
    insightsQueryOverview,
    recentSegments,
    remarkTags,
    firmWorkflows,
  } = dataset
  // The page-singleton pinia, passed explicitly: entry points call this
  // before any app.use(pinia) has set an active instance.
  useInsightsRootStore(getPiniaInstance()).init({
    allCompaniesCount: allCompaniesCount ? parseInt(allCompaniesCount, 10) : 0,
    lite: liteInsights === 'true',
    queryOverviewEnabled: insightsQueryOverview === 'true',
    recentSegments: (recentSegments
      ? JSON.parse(recentSegments)
      : []) as number[],
    remarkTags: (remarkTags ? JSON.parse(remarkTags) : {}) as Record<
      string,
      unknown
    >,
    firmWorkflows: (firmWorkflows
      ? JSON.parse(firmWorkflows)
      : []) as unknown[],
  })
}

export const useInsightsRootStore = defineStore('insights-root', () => {
  const ledgerSelectDate = ref<string | null>(null)
  const ledgerSelectLockType = ref<string | null>(null)
  const periodSelection = ref<string | null>(null)
  const periodSelectionOpen = ref(false)
  const isPremiumInfoModalOpen = ref(false)
  const queryOverviewEnabled = ref(false)
  const recentSegments = ref<number[]>([])
  const existingRequestRef = ref<string | null>(null)
  const lite = ref(false)
  const remarkTags = ref<Record<string, unknown>>({})
  const firmWorkflows = ref<unknown[]>([])
  const allCompaniesCount = ref<number | undefined>(undefined)
  // True while the page holds an unsaved query opened from "+ New query", so
  // the header and the breadcrumb name it "New query" instead of "All clients".
  const newQuery = ref(false)

  function init(params: InsightsRootInit): void {
    lite.value = params.lite ?? false
    queryOverviewEnabled.value = params.queryOverviewEnabled ?? false
    recentSegments.value = params.recentSegments ?? []
    remarkTags.value = params.remarkTags ?? {}
    firmWorkflows.value = params.firmWorkflows ?? []
    allCompaniesCount.value = params.allCompaniesCount
  }

  function setLedgerSelectDate(value: string | null): void {
    ledgerSelectDate.value = value ? value : null
  }

  function setLedgerSelectLockType(value: string | null): void {
    ledgerSelectLockType.value = value
  }

  function setPeriodSelection(value: string | null): void {
    periodSelection.value = value
  }

  function queuePeriodSelectionOpen(): void {
    periodSelectionOpen.value = true
  }

  function resetPeriodSelectionOpen(): void {
    periodSelectionOpen.value = false
  }

  function togglePremiumModal(): void {
    isPremiumInfoModalOpen.value = !isPremiumInfoModalOpen.value
  }

  function addRecentSegment(segmentId: number): void {
    recentSegments.value.unshift(segmentId)
  }

  function setExistingRequestRef(value: string | null): void {
    existingRequestRef.value = value
  }

  function setNewQuery(value: boolean): void {
    newQuery.value = value
  }

  return {
    ledgerSelectDate,
    ledgerSelectLockType,
    periodSelection,
    periodSelectionOpen,
    isPremiumInfoModalOpen,
    queryOverviewEnabled,
    recentSegments,
    existingRequestRef,
    lite,
    remarkTags,
    firmWorkflows,
    allCompaniesCount,
    newQuery,
    init,
    setLedgerSelectDate,
    setLedgerSelectLockType,
    setPeriodSelection,
    queuePeriodSelectionOpen,
    resetPeriodSelectionOpen,
    togglePremiumModal,
    addRecentSegment,
    setExistingRequestRef,
    setNewQuery,
  }
})
